/**
 * 테마 선택을 React 로 노출한다. 실제 적용·저장은 전부 `theme.ts` 가 한다 —
 * 이 파일은 «상태» 만 들고 있고 규칙을 복제하지 않는다.
 *
 * ## 🔴 초기값을 «기본값» 으로 두지 않는다
 *
 * `<head>` 의 인라인 스크립트가 이미 `<html>` 에 테마를 적용한 뒤에 React 가 뜬다.
 * 그때 상태를 `'system'` 으로 시작하면 **화면과 상태가 어긋난 한 프레임**이 생기고,
 * 토글 아이콘이 잘못된 값을 가리킨다. 그래서 초기값을 **DOM 에서 읽는다**
 * (`data-theme`) — 그게 그 순간 참인 유일한 값이다.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  applyTheme,
  isTheme,
  readTheme,
  resolveTheme,
  subscribeSystemTheme,
  writeTheme,
  type ResolvedTheme,
  type Theme,
} from './theme';

interface ThemeContextValue {
  /** 사용자가 고른 값(`system` 포함). */
  theme: Theme;
  /** 지금 화면에 실제로 적용된 값. */
  resolved: ResolvedTheme;
  setTheme: (theme: Theme) => void;
  /** light ↔ dark 를 오간다. `system` 이었으면 «지금의 반대» 로 고정된다. */
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

/**
 * 인라인 스크립트가 심어 둔 값을 읽는다. 없으면(스크립트 실패·SSR) 저장소로 떨어지고,
 * 그것도 없으면 `defaultTheme`.
 */
function initialTheme(defaultTheme: Theme): Theme {
  const attr = globalThis.document?.documentElement?.dataset?.theme;
  if (isTheme(attr)) return attr;
  const stored = readTheme();
  // readTheme 은 «없음» 을 'system' 으로 돌려주므로, 앱 기본값이 다른 경우
  // (에디터=dark) 저장된 적이 없는 것과 구별할 수 없다. 저장소를 직접 한 번 더 본다.
  return stored === 'system' && defaultTheme !== 'system' ? defaultTheme : stored;
}

export function ThemeProvider({
  children,
  defaultTheme = 'system',
}: {
  children: ReactNode;
  /** 저장된 선택이 없을 때의 값. 에디터는 `dark` 로 준다. */
  defaultTheme?: Theme;
}) {
  const [theme, setThemeState] = useState<Theme>(() => initialTheme(defaultTheme));
  const [resolved, setResolved] = useState<ResolvedTheme>(() =>
    resolveTheme(initialTheme(defaultTheme))
  );

  // 마운트 시 한 번 맞춘다. 인라인 스크립트가 못 돌았거나(차단·구형) 저장값과 DOM 이
  // 어긋난 경우를 여기서 수습한다 — 「스크립트가 돌았겠지」를 가정하지 않는다.
  useEffect(() => {
    setResolved(applyTheme(theme));
  }, [theme]);

  // `system` 일 때만 OS 를 따라간다. 사용자가 명시적으로 고른 뒤에는 OS 가 바뀌어도
  // 화면이 멋대로 바뀌지 않는다 — 그게 「고정」의 뜻이다.
  useEffect(() => {
    if (theme !== 'system') return;
    return subscribeSystemTheme(() => setResolved(applyTheme('system')));
  }, [theme]);

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next);
    writeTheme(next);
    setResolved(applyTheme(next));
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState((cur) => {
      const next: Theme = resolveTheme(cur) === 'dark' ? 'light' : 'dark';
      writeTheme(next);
      setResolved(applyTheme(next));
      return next;
    });
  }, []);

  const value = useMemo(
    () => ({ theme, resolved, setTheme, toggleTheme }),
    [theme, resolved, setTheme, toggleTheme]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

/**
 * ⚠️ 프로바이더 밖에서 부르면 **던진다.** 조용히 기본값을 돌려주면 토글이 «눌리는데
 * 아무 일도 안 일어나는» 상태가 되고, 그건 배선 실수를 영원히 숨긴다.
 */
export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within <ThemeProvider>');
  return ctx;
}
